import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { restaurantAPI } from '../../services/api';

// Flutter App Theme Colors (exact match)
const theme = {
  primaryColor: '#FF6961',
  primaryLight: '#FF8A84',
  primaryDark: '#E6524A',
  backgroundColor: '#F8FAFC',
  surfaceColor: '#FFFFFF',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  successColor: '#22C55E',
  warningColor: '#F59E0B',
  errorColor: '#EF4444',
  infoColor: '#3B82F6',
};

function SubscriptionInfo() {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renewLoading, setRenewLoading] = useState(false);
  const [daysUntilExpiry, setDaysUntilExpiry] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [cityMonthlyFee, setCityMonthlyFee] = useState(999);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const response = await restaurantAPI.getSubscriptionPlanStatus();
      const data = response.data.data;
      setSubscription(data);

      // Calculate days until expiry
      if (data?.subscriptionPlan?.monthly?.currentPeriodEnd) {
        const endDate = new Date(data.subscriptionPlan.monthly.currentPeriodEnd);
        const today = new Date();
        const diffTime = endDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setDaysUntilExpiry(diffDays);
        setIsExpired(diffDays <= 0);
      }

      // Set city monthly fee
      if (data?.cityMonthlyFee) {
        setCityMonthlyFee(data.cityMonthlyFee);
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleRenew = async () => {
    setRenewLoading(true);
    try {
      // Navigate to payment page for renewal
      navigate('/restaurant/subscription-payment?renew=true');
    } catch (error) {
      console.error('Failed to initiate renewal:', error);
      alert('Failed to initiate renewal. Please try again.');
    } finally {
      setRenewLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        background: theme.backgroundColor,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{
            width: '40px',
            height: '40px',
            border: `3px solid ${theme.primaryColor}20`,
            borderTop: `3px solid ${theme.primaryColor}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: theme.textSecondary }}>Loading subscription info...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const plan = subscription?.subscriptionPlan;
  const monthly = plan?.monthly;

  // Get status info
  let statusText = 'No Active Subscription';
  let statusColor = theme.textSecondary;
  let statusIcon = '❌';

  if (plan?.type === 'monthly') {
    switch (monthly?.status) {
      case 'active':
        statusText = 'Active';
        statusColor = theme.successColor;
        statusIcon = '✅';
        break;
      case 'trial':
        statusText = 'Free Trial';
        statusColor = theme.infoColor;
        statusIcon = '🎁';
        break;
      case 'expired':
        statusText = 'Expired';
        statusColor = theme.errorColor;
        statusIcon = '⚠️';
        break;
      case 'pending':
        statusText = 'Payment Pending';
        statusColor = theme.warningColor;
        statusIcon = '⏳';
        break;
      default:
        statusText = 'Unknown';
    }
  }

  return (
    <div style={{ background: theme.backgroundColor, minHeight: '100vh' }}>
      <div style={{ padding: '16px' }}>
        {/* Subscription Status Card - matches Flutter _buildSubscriptionStatusCard */}
        <div style={{
          background: `linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.primaryDark} 100%)`,
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          color: 'white'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>{statusIcon}</span>
            <div style={{ flex: 1 }}>
              <p style={{
                margin: '0 0 4px 0',
                fontSize: '14px',
                opacity: 0.8
              }}>
                Monthly Subscription
              </p>
              <h2 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '700'
              }}>
                {statusText}
              </h2>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              ₹{cityMonthlyFee}/month
            </div>
          </div>

          {/* Days until expiry */}
          {daysUntilExpiry !== null && monthly?.status !== 'pending' && (
            <div style={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ opacity: 0.8 }}>
                {isExpired ? 'Expired' : 'Expires in'}
              </span>
              <span style={{
                fontWeight: '700',
                fontSize: '16px',
                color: isExpired || (daysUntilExpiry <= 7) ? '#FEF08A' : 'white'
              }}>
                {isExpired ? 'Please renew' : `${daysUntilExpiry} days`}
              </span>
            </div>
          )}

          {/* Renew button */}
          {(isExpired || (daysUntilExpiry !== null && daysUntilExpiry <= 7)) && (
            <button
              onClick={handleRenew}
              disabled={renewLoading}
              style={{
                width: '100%',
                marginTop: '16px',
                padding: '12px',
                background: 'white',
                color: theme.primaryColor,
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: renewLoading ? 'not-allowed' : 'pointer',
                opacity: renewLoading ? 0.7 : 1
              }}
            >
              {renewLoading ? 'Processing...' : (isExpired ? 'Renew Now' : 'Renew Early')}
            </button>
          )}
        </div>

        {/* Subscription Details - matches Flutter _buildDetailsSection */}
        {plan?.type === 'monthly' && monthly && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: theme.textPrimary,
              marginBottom: '12px'
            }}>
              Subscription Details
            </h3>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              overflow: 'hidden'
            }}>
              {monthly.currentPeriodStart && (
                <DetailRow
                  label="Current Period Start"
                  value={formatDate(monthly.currentPeriodStart)}
                />
              )}
              {monthly.currentPeriodEnd && (
                <DetailRow
                  label="Current Period End"
                  value={formatDate(monthly.currentPeriodEnd)}
                />
              )}
              {monthly.status === 'trial' && monthly.trialEndDate && (
                <DetailRow
                  label="Trial Ends"
                  value={formatDate(monthly.trialEndDate)}
                  highlight
                />
              )}
              {monthly.lastPaymentDate && (
                <DetailRow
                  label="Last Payment"
                  value={`₹${monthly.lastPaymentAmount || 0} on ${formatDate(monthly.lastPaymentDate)}`}
                />
              )}
              <DetailRow
                label="Auto Renew"
                value={monthly.autoRenew ? 'Enabled' : 'Disabled'}
                isLast
              />
            </div>
          </div>
        )}

        {/* Benefits Section - matches Flutter _buildBenefitsCard */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: theme.textPrimary,
            marginBottom: '12px'
          }}>
            Your Benefits
          </h3>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
          }}>
            <BenefitRow icon="💰" title="0% Commission" description="Keep 100% of your order earnings" />
            <BenefitRow icon="♾️" title="Unlimited Orders" description="No limits on order volume" />
            <BenefitRow icon="🎧" title="Priority Support" description="24/7 customer support" />
            <BenefitRow icon="📊" title="Full Analytics" description="Access all business insights" isLast />
          </div>
        </div>

        {/* Info Box */}
        <div style={{
          background: '#EFF6FF',
          borderRadius: '12px',
          padding: '16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>ℹ️</span>
          <p style={{
            margin: 0,
            fontSize: '13px',
            color: theme.infoColor,
            lineHeight: 1.5
          }}>
            Your subscription includes 0% commission on all orders. Full profits are yours!
          </p>
        </div>
      </div>
    </div>
  );
}

// Detail Row Component
function DetailRow({ label, value, highlight = false, isLast = false }) {
  return (
    <div style={{
      padding: '12px 16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: isLast ? 'none' : '1px solid #F1F5F9'
    }}>
      <span style={{
        fontSize: '14px',
        color: '#64748B'
      }}>
        {label}
      </span>
      <span style={{
        fontSize: '14px',
        fontWeight: '500',
        color: highlight ? '#3B82F6' : '#0F172A'
      }}>
        {value}
      </span>
    </div>
  );
}

// Benefit Row Component - matches Flutter _buildBenefitRow
function BenefitRow({ icon, title, description, isLast = false }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: isLast ? 0 : '12px'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        background: '#FF696115',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
        flexShrink: 0
      }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <p style={{
          margin: 0,
          fontWeight: '600',
          fontSize: '14px',
          color: '#0F172A'
        }}>
          {title}
        </p>
        <p style={{
          margin: '2px 0 0 0',
          fontSize: '12px',
          color: '#64748B'
        }}>
          {description}
        </p>
      </div>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="#22C55E">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
      </svg>
    </div>
  );
}

export default SubscriptionInfo;
